import { trackAction } from '@cint/analytics';
import {
  ActionMenu,
  ActionMenuContent,
  ActionMenuItem,
  ActionMenuTriggerType,
  MenuItems,
  NextTabs,
  NextTabsList,
  NextTabsTrigger,
} from '@cint/wonderment';
import type { Options } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useMemo, useRef, useState } from 'react';

import { EVENTS } from '@/analytics';
import { ChartHeader } from '@/components/Charts';
import Highcharts from '@/utils/chartSetup';
import { deepMerge } from '@/utils/deepMerge';
import { buildSharedTooltip } from '@/utils/reports/chartDataUtils';

interface Series {
  name?: string;
  stack?: string;
  data: { y: number; color?: string }[];
}

type SeriesByTab = Record<string, Series[]>;

type CategoriesByTab = Record<string, string[]>;

interface TabConfig {
  label: string;
  value: string;
}

interface BaseChartProps {
  title: string;
  series: Series[] | SeriesByTab;
  categories?: string[] | CategoriesByTab;
  chartType?: 'column' | 'line' | 'bar';
  showDataLabels?: boolean;
  customOptions?: ExtendedHighchartsOptions;
  subtitle?: string;
  headerTooltip?: string;
  hasTotal?: boolean;
  formatValue?: (val: number | string) => string | JSX.Element;
  headerStats?: { label: string; value: number }[];
  tooltipFormatter?: Highcharts.TooltipFormatterCallbackFunction;
  children?: React.ReactNode;
  tabsList?: TabConfig[];
  isLegendEnabled?: boolean;
  titleClassName?: string;
}

type ExtendedHighchartsOptions = Options & {
  exportData?: {
    enabled?: boolean;
    [key: string]: any;
  };
};

// Safely extract the component whether it's the default export or nested under .default
const HighchartsReactComponent =
  (HighchartsReact as any).default || HighchartsReact;

const computeSeriesTotals = (series: Series[]) => {
  return series.map((s) => {
    const total = s.data?.reduce((acc, point) => acc + (point.y || 0), 0);
    return { ...s, total };
  });
};

const createSharedTooltipFormatter = (
  formatValue: (val: number) => string | JSX.Element = (val) => val.toString()
) => {
  return function (this: any) {
    return buildSharedTooltip(this, formatValue);
  };
};

const buildDefaultChartOptions = (
  chartType: BaseChartProps['chartType'],
  categories: string[],
  showDataLabels: boolean,
  hasMultipleSeries: boolean,
  seriesWithTotals: (Series & { total: number })[],
  formatValue?: (val: number) => string | JSX.Element,
  tooltipFormatter?: Highcharts.TooltipFormatterCallbackFunction
): ExtendedHighchartsOptions => {
  const chartRef = {
    hoveredStackName: null as string | null,
  };

  const enhancedTooltipFormatter = function (this: any) {
    if (tooltipFormatter) {
      const enhancedContext = {
        ...this,
        hoveredStackName: chartRef.hoveredStackName,
      };

      return tooltipFormatter.call(enhancedContext);
    }

    return createSharedTooltipFormatter(formatValue).call(this);
  };

  const pointEvents =
    chartType == 'column'
      ? {
          mouseOver: function () {
            chartRef.hoveredStackName = this.series?.options?.stack;
          },
          mouseOut: function () {
            chartRef.hoveredStackName = null;
          },
        }
      : {};

  return {
    chart: {
      type: chartType,
      ...(hasMultipleSeries ? {} : { spacingTop: 40 }),
    },
    title: { text: '' },
    legend: {
      align: 'left',
      verticalAlign: 'top',
      shadow: false,
      symbolRadius: 0,
      enabled: hasMultipleSeries,
      labelFormatter: function () {
        const total = new Intl.NumberFormat().format(
          Math.abs((this.options as any).custom?.total ?? 0)
        );
        const name = (this.options as any)?.stack || this.name;
        return `
        <span style="font-size: 14px; font-weight: 600;">${name}</span><br>
        <strong style="font-size: 16px; font-weight: 800;">${total}</strong>
      `;
      },
      itemStyle: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#50405E',
      },
      useHTML: true,
      events: {
        itemClick: function (event) {
          event.preventDefault?.();
        },
      },
    },
    tooltip: {
      useHTML: true,
      shared: true,
      formatter: enhancedTooltipFormatter,
    },
    colors: [
      '#6E3FA3',
      '#F35E5D',
      '#EE9847',
      '#C7AE8E',
      '#489295',
      '#F3C453',
      '#3D88C8',
      '#59AC77',
      '#FD94A8',
      '#8CAECC',
      '#8BB368',
      '#989190',
    ],
    xAxis: {
      categories,
      lineColor: '#BEB4B4',
      gridLineColor: 'rgba(36, 4, 4, 0.07)',
    },
    yAxis: {
      title: undefined,
      gridLineColor: 'rgba(36, 4, 4, 0.07)',
      labels: {
        formatter: function () {
          return formatValue
            ? `${formatValue(this.value as number)}`
            : `${this.value}`;
        },
        style: {
          fontSize: '12px',
          fontWeight: '400',
          color: '#50405E',
        },
      },
    },
    plotOptions: {
      [chartType]: {
        dataLabels: {
          enabled: showDataLabels,
          formatter: function () {
            return this.y !== 0 && formatValue ? formatValue(this.y) : null;
          },
          inside: false,
          style: {
            fontSize: '14px',
            fontWeight: '600',
            color: '#50405E',
          },
        },
        point: {
          events: pointEvents,
        },
      },
      series: {
        marker: chartType === 'line' ? { enabled: false } : undefined,
      },
    },
    series: seriesWithTotals.map((element, index, arr) => ({
      name: element.name,
      type: chartType,
      data: element.data,
      stack: element.stack,
      custom: {
        total: element.total,
      },
      showInLegend:
        !element.stack ||
        index === arr.findIndex((s) => s.stack === element.stack),
    })),
    credits: { enabled: false },
    exporting: {
      enabled: true,
      buttons: {
        contextButton: {
          enabled: false,
        },
      },
    },
    exportData: {
      enabled: true,
    },
  };
};

export const BaseChart = ({
  title,
  series,
  categories,
  chartType,
  showDataLabels = chartType === 'column',
  customOptions,
  subtitle,
  headerTooltip,
  hasTotal = true,
  formatValue,
  headerStats,
  tooltipFormatter,
  tabsList,
  titleClassName,
}: BaseChartProps) => {
  const shouldCalculateTotal = hasTotal && !headerStats;
  const [selectedTab, setSelectedTab] = useState(tabsList?.[0]?.value);
  const currentSeries =
    tabsList && series?.[selectedTab] ? series[selectedTab] : series;
  const currentCategories =
    tabsList && categories?.[selectedTab]
      ? categories[selectedTab]
      : categories;
  const currentStacks = [
    ...new Set(currentSeries.map((obj) => obj.stack).filter(Boolean)),
  ];
  const seriesWithTotals = useMemo(
    () => computeSeriesTotals(currentSeries),
    [currentSeries]
  );
  const total = useMemo(
    () =>
      shouldCalculateTotal &&
      seriesWithTotals.reduce((acc, s) => acc + s.total, 0),
    [seriesWithTotals, shouldCalculateTotal]
  );

  const hasMultipleSeries =
    currentSeries.length > 1 && currentStacks.length !== 1;
  const isSmall =
    !hasMultipleSeries && (!currentCategories || currentCategories.length < 7);

  const defaultOptions = useMemo(
    () =>
      buildDefaultChartOptions(
        chartType,
        currentCategories,
        showDataLabels,
        hasMultipleSeries,
        seriesWithTotals,
        formatValue,
        tooltipFormatter
      ),
    [
      chartType,
      currentCategories,
      showDataLabels,
      hasMultipleSeries,
      seriesWithTotals,
      formatValue,
      tooltipFormatter,
    ]
  );

  const options: ExtendedHighchartsOptions = useMemo(
    () => deepMerge({ ...defaultOptions }, customOptions),
    [defaultOptions, customOptions]
  );

  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  const handleExport = (action) => {
    if (action === 'downloadCsv') {
      trackAction(EVENTS.REPORTING_CHARTS_DOWNLOADED);
      chartComponentRef.current?.chart.downloadCSV();
    }
    if (action === 'fullScreen') {
      chartComponentRef.current?.chart.fullscreen.toggle();
    }
  };

  return (
    <div className={isSmall ? 'col-span-1' : 'col-span-2'}>
      <div className="pl-2 flex justify-between items-end">
        <ChartHeader
          title={title}
          subtitle={subtitle}
          headerTooltip={headerTooltip}
          hasTotal={hasTotal}
          total={total}
          formatValue={formatValue}
          headerStats={headerStats}
          titleClassName={titleClassName}
          hasMultipleSeries={hasMultipleSeries}
        />
        <div>
          <ActionMenu
            triggerProps={{
              props: {
                iconName: 'Dots',
              },
              type: ActionMenuTriggerType.ButtonIcon,
            }}
          >
            <ActionMenuContent>
              <MenuItems>
                {[
                  {
                    label: 'Download to CSV',
                    value: 'downloadCsv',
                  },
                  {
                    label: 'View in full screen',
                    value: 'fullScreen',
                  },
                ].map((option) => (
                  <ActionMenuItem
                    key={option.value}
                    onClick={() => handleExport(option.value)}
                    label={option.label}
                  />
                ))}
              </MenuItems>
            </ActionMenuContent>
          </ActionMenu>
        </div>
      </div>
      {tabsList?.length > 0 && (
        <NextTabs value={selectedTab} onValueChange={setSelectedTab}>
          <NextTabsList className="mb-6">
            {tabsList.map(({ label, value }) => (
              <NextTabsTrigger key={value} value={value}>
                {label}
              </NextTabsTrigger>
            ))}
          </NextTabsList>
        </NextTabs>
      )}
      <HighchartsReactComponent
        highcharts={Highcharts}
        options={options}
        ref={chartComponentRef}
      />
    </div>
  );
};
